import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { 
  Mail, Phone, CheckCircle, XCircle, Clock, Search, UserSearch, Archive
} from "lucide-react";
import { CSSProperties, useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { hasAnyRole } from "@/lib/roles";

const DAYS_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

const STATUS_CONFIG = {
  pending: { label: "Ausstehend", variant: "secondary" as const, icon: Clock },
  confirmed: { label: "Bestätigt", variant: "default" as const, icon: CheckCircle },
  cancelled: { label: "Storniert", variant: "outline" as const, icon: XCircle },
  waitlist: { label: "Warteliste", variant: "outline" as const, icon: Clock },
};

export default function AdminBookingHistory() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) return <DashboardLayoutSkeleton />;
  if (!user || !hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold">Zugriff verweigert</h1>
          <Link href="/"><Button>Zur Startseite</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <AdminBookingHistoryContent setSidebarWidth={setSidebarWidth} />
    </SidebarProvider>
  );
}

function AdminBookingHistoryContent({ setSidebarWidth }: { setSidebarWidth: (width: number) => void }) {
  const isMobile = useIsMobile();

  // Get email from URL query parameter
  const searchParams = useSearch();
  const urlEmail = new URLSearchParams(searchParams).get("email") || "";

  const [searchEmail, setSearchEmail] = useState(urlEmail);
  const [searchQuery, setSearchQuery] = useState(urlEmail);

  // Auto-search when URL parameter changes
  useEffect(() => {
    if (urlEmail) {
      setSearchEmail(urlEmail);
      setSearchQuery(urlEmail);
    }
  }, [urlEmail]);

  // Only search when user submits or URL parameter is present
  const { data: bookings, isLoading, isFetching } = trpc.bookingHistory.searchByEmail.useQuery(
    { email: searchQuery },
    { enabled: searchQuery.length > 0 }
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchEmail.trim()) {
      setSearchQuery(searchEmail.trim());
    }
  };

  // Calculate statistics
  const stats = bookings ? {
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === "confirmed").length,
    pending: bookings.filter(b => b.status === "pending").length,
    cancelled: bookings.filter(b => b.status === "cancelled").length,
    waitlist: bookings.filter(b => b.status === "waitlist").length,
  } : null;

  return (
    <>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Buchungsverlauf</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground">Buchungsverlauf</h1>
            <p className="text-muted-foreground">Suchen Sie nach Teilnehmer-Buchungen per E-Mail</p>
          </div>

          {/* Search Form */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserSearch className="h-5 w-5" />
                Teilnehmer suchen
              </CardTitle>
              <CardDescription>
                Geben Sie eine E-Mail-Adresse ein, um alle Buchungen dieses Teilnehmers anzuzeigen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="E-Mail-Adresse eingeben..."
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button type="submit" disabled={!searchEmail.trim() || isFetching}>
                  <Search className="h-4 w-4 mr-2" />
                  Suchen
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {searchQuery && (
            <>
              {/* Statistics */}
              {stats && stats.total > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="text-2xl font-bold">{stats.total}</div>
                      <p className="text-xs text-muted-foreground">Gesamt</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="text-2xl font-bold text-green-600">{stats.confirmed}</div>
                      <p className="text-xs text-muted-foreground">Bestätigt</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                      <p className="text-xs text-muted-foreground">Ausstehend</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="text-2xl font-bold text-blue-600">{stats.waitlist}</div>
                      <p className="text-xs text-muted-foreground">Warteliste</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4">
                      <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
                      <p className="text-xs text-muted-foreground">Storniert</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
                </div>
              )}

              {/* Results Table */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    Buchungen für "{searchQuery}"
                  </CardTitle>
                  <CardDescription>
                    {bookings?.length || 0} Buchungen gefunden
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!isLoading && bookings && bookings.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Kurs</TableHead>
                            <TableHead>Tag/Zeit</TableHead>
                            <TableHead>Teilnehmer</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Anmeldedatum</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bookings.map((booking) => {
                            const statusConfig = STATUS_CONFIG[booking.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;
                            const StatusIcon = statusConfig.icon;
                            return (
                              <TableRow key={booking.id}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{booking.course?.name || "Unbekannt"}</span>
                                    {!!booking.course?.isArchived && (
                                      <span title="Archiviert">
                                        <Archive className="h-3 w-3 text-amber-600" />
                                      </span>
                                    )}
                                  </div>
                                  {booking.instructor && (
                                    <div className="text-sm text-muted-foreground">
                                      {booking.instructor.name}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {booking.course && (
                                    <div>
                                      <div className="font-medium">{DAYS_DE[booking.course.dayOfWeek]}</div>
                                      <div className="text-sm text-muted-foreground">
                                        {booking.course.startTime} - {booking.course.endTime}
                                      </div>
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="font-medium">{booking.participantName}</div>
                                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {booking.participantEmail}
                                  </div>
                                  {booking.participantPhone && (
                                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                                      <Phone className="h-3 w-3" />
                                      {booking.participantPhone}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge 
                                    variant={statusConfig.variant}
                                    className={
                                      booking.status === "waitlist" 
                                        ? "bg-blue-100 text-blue-800 border-blue-200" 
                                        : booking.status === "confirmed"
                                          ? "bg-green-100 text-green-800 border-green-200"
                                          : ""
                                    }
                                  >
                                    <StatusIcon className="h-3 w-3 mr-1" />
                                    {statusConfig.label}
                                    {booking.status === "waitlist" && booking.waitlistPosition && (
                                      <span className="ml-1">#{booking.waitlistPosition}</span>
                                    )}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="text-sm">
                                    {format(new Date(booking.createdAt), "dd.MM.yyyy", { locale: de })}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {format(new Date(booking.createdAt), "HH:mm", { locale: de })} Uhr
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <UserSearch className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Keine Buchungen für diese E-Mail-Adresse gefunden.</p>
                      <p className="text-sm mt-2">Versuchen Sie es mit einer anderen E-Mail-Adresse.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Initial State */}
          {!searchQuery && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Geben Sie eine E-Mail-Adresse ein, um die Buchungshistorie anzuzeigen.</p>
                  <p className="text-sm mt-2">Die Suche findet auch Teilübereinstimmungen.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </SidebarInset>
    </>
  );
}
